import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { LawEnforcementProfile, LawEnforcementProfileFormData } from '../../types';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

interface LawEnforcementProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile?: LawEnforcementProfile;
  onProfileUpdate?: (profile: LawEnforcementProfile) => void;
  mode?: 'view' | 'edit';
}

export const LawEnforcementProfileModal: React.FC<LawEnforcementProfileModalProps> = ({
  isOpen,
  onClose,
  profile,
  onProfileUpdate,
  mode = 'view'
}) => {
  const { user } = useAuth();
  const [currentMode, setCurrentMode] = useState<'view' | 'edit'>(mode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'email' | 'office' | 'contact' | 'document'>('email');

  // Form data state
  const [formData, setFormData] = useState<LawEnforcementProfileFormData>({
    departmentName: '',
    rankPosition: '',
    officeAddress: '',
    whatsappNumber: '',
    telegramNumber: ''
  });

  // Email verification state
  const [emailData, setEmailData] = useState({
    email: '',
    otp: '',
    step: 'email' as 'email' | 'otp'
  });

  // File upload state
  const [fileUpload, setFileUpload] = useState({
    file: null as File | null,
    isUploading: false
  });

  // Field validation state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Helper function to create default profile
  const createDefaultProfile = (overrides: Partial<LawEnforcementProfile> = {}): LawEnforcementProfile => ({
    id: 0,
    isLawEnforcement: true,
    verificationStatus: 'not_applied',
    governmentEmail: '',
    governmentEmailVerified: false,
    departmentName: '',
    rankPosition: '',
    officeAddress: '',
    whatsappNumber: '',
    telegramNumber: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...overrides
  });

  // Initialize form data when profile changes
  useEffect(() => {
    if (profile) {
      setFormData({
        departmentName: profile.departmentName || '',
        rankPosition: profile.rankPosition || '',
        officeAddress: profile.officeAddress || '',
        whatsappNumber: profile.whatsappNumber || '',
        telegramNumber: profile.telegramNumber || ''
      });
      setEmailData(prev => ({
        ...prev,
        email: profile.governmentEmail || '',
        step: profile.governmentEmailVerified ? 'otp' : 'email'
      }));
    } else {
      // Reset form data when no profile
      setFormData({
        departmentName: '',
        rankPosition: '',
        officeAddress: '',
        whatsappNumber: '',
        telegramNumber: ''
      });
      setEmailData({
        email: '',
        otp: '',
        step: 'email'
      });
    }
  }, [profile]);

  // Reset modal state when opening/closing
  useEffect(() => {
    if (isOpen) {
      setCurrentMode(mode);
      setError(null);
      setSuccess(null);
      setFieldErrors({});
      // Start with email tab to give users choice of verification method
      if (profile?.governmentEmailVerified) {
        setActiveTab('office');
      } else {
        setActiveTab('email'); // Start with email tab to show verification options
      }
    }
  }, [isOpen, mode, profile]);

  const handleInputChange = (field: keyof LawEnforcementProfileFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEmailChange = (field: 'email' | 'otp', value: string) => {
    setEmailData(prev => ({ ...prev, [field]: value }));
  };

  const handleCopyNumber = (fromField: 'whatsappNumber' | 'telegramNumber', toField: 'whatsappNumber' | 'telegramNumber') => {
    const valueToCopy = formData[fromField];
    if (valueToCopy) {
      setFormData(prev => ({ ...prev, [toField]: valueToCopy }));
      setSuccess(`${fromField === 'whatsappNumber' ? 'WhatsApp' : 'Telegram'} number copied to ${toField === 'whatsappNumber' ? 'WhatsApp' : 'Telegram'}`);
    }
  };

  const handleSendOTP = async () => {
    if (!emailData.email) {
      setError('Email address is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiService.sendGovernmentEmailOTP({ email: emailData.email });
      setEmailData(prev => ({ ...prev, step: 'otp' }));
      setSuccess('OTP sent successfully to your email');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!emailData.otp || emailData.otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiService.verifyGovernmentEmailOTP({
        email: emailData.email,
        otp: emailData.otp
      });
      setSuccess('Email verified successfully!');
      
      // Update profile with verified email
      if (onProfileUpdate) {
        const updatedProfile = profile ? {
          ...profile,
          governmentEmail: emailData.email,
          governmentEmailVerified: true
        } : createDefaultProfile({
          governmentEmail: emailData.email,
          governmentEmailVerified: true
        });
        onProfileUpdate(updatedProfile);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetCurrentOTP = async () => {
    try {
      const otpData = await apiService.getCurrentOTP();
      alert(`Current OTP: ${otpData.otp}\nEmail: ${otpData.email}\nExpires: ${new Date(otpData.expires_at).toLocaleString()}`);
    } catch (error) {
      console.error('Error getting OTP:', error);
      alert('No valid OTP found or OTP has expired');
    }
  };

  const handleFileUpload = async (file: File) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('government_id_file', file);
      
      const response = await apiService.uploadGovernmentId(formData);
      setSuccess('Government ID uploaded successfully');
      
      // Update profile with file URL
      if (onProfileUpdate) {
        const updatedProfile = profile ? {
          ...profile,
          governmentIdFile: response.file_url
        } : createDefaultProfile({
          governmentIdFile: response.file_url
        });
        onProfileUpdate(updatedProfile);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const errors: string[] = [];
    const newFieldErrors: Record<string, string> = {};

    // Validate office details
    if (!formData.departmentName.trim()) {
      errors.push('Department name is required');
      newFieldErrors.departmentName = 'Department name is required';
    }
    if (!formData.rankPosition.trim()) {
      errors.push('Rank/Position is required');
      newFieldErrors.rankPosition = 'Rank/Position is required';
    }
    if (!formData.officeAddress.trim()) {
      errors.push('Office address is required');
      newFieldErrors.officeAddress = 'Office address is required';
    }

    // Validate phone numbers if provided
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (formData.whatsappNumber && !phoneRegex.test(formData.whatsappNumber)) {
      errors.push('WhatsApp number must be in international format (+1234567890)');
      newFieldErrors.whatsappNumber = 'Must be in international format (+1234567890)';
    }
    if (formData.telegramNumber && !phoneRegex.test(formData.telegramNumber)) {
      errors.push('Telegram number must be in international format (+1234567890)');
      newFieldErrors.telegramNumber = 'Must be in international format (+1234567890)';
    }

    // Validate email if provided
    if (emailData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailData.email)) {
      errors.push('Please enter a valid email address');
      newFieldErrors.email = 'Please enter a valid email address';
    }

    // Check if either government email verification OR government ID is provided
    const hasGovernmentEmailVerified = profile?.governmentEmailVerified;
    const hasGovernmentId = profile?.governmentIdFile;
    
    if (!hasGovernmentEmailVerified && !hasGovernmentId) {
      errors.push('Either government email verification or government ID document is required for verification');
    }

    // Update field errors state
    setFieldErrors(newFieldErrors);

    return errors;
  };

  const handleSaveProfile = async () => {
    // Validate form before submission
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (profile) {
        // Update existing profile
        const updatedProfile = await apiService.updateLawEnforcementProfile(formData);
        onProfileUpdate?.(updatedProfile);
        setSuccess('Profile updated successfully');
        setCurrentMode('view');
        
        // Refresh the profile data
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        // Create new profile first, then update it with form data
        const newProfile = await apiService.createLawEnforcementProfile();
        const updatedProfile = await apiService.updateLawEnforcementProfile(formData);
        onProfileUpdate?.(updatedProfile);
        setSuccess('Law enforcement profile created successfully');
        setCurrentMode('view');
        
        // Refresh the profile data
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (err: any) {
      // Handle specific error types
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to save profile. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForVerification = async () => {
    // Validate form before submission
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    // Check if at least one verification method is completed
    const hasGovernmentEmailVerified = profile?.governmentEmailVerified;
    const hasGovernmentId = profile?.governmentIdFile;
    
    if (!hasGovernmentEmailVerified && !hasGovernmentId) {
      setError('Please complete either email verification or upload a government ID document before submitting for verification.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // First save the profile data
      let currentProfile = profile;
      if (!currentProfile) {
        currentProfile = await apiService.createLawEnforcementProfile();
      } else {
        currentProfile = await apiService.updateLawEnforcementProfile(formData);
      }

      // Then submit for verification
      const verificationData = {
        departmentName: formData.departmentName,
        rankPosition: formData.rankPosition,
        officeAddress: formData.officeAddress,
        whatsappNumber: formData.whatsappNumber,
        telegramNumber: formData.telegramNumber,
        governmentEmail: currentProfile.governmentEmail || '',
        governmentEmailVerified: currentProfile.governmentEmailVerified,
        governmentIdFile: currentProfile.governmentIdFile
      };

      const submittedProfile = await apiService.submitLawEnforcementVerification(verificationData);
      onProfileUpdate?.(submittedProfile);
      setSuccess('Profile submitted for verification successfully! Your application will be reviewed by our admin team.');
      setCurrentMode('view');
      
      // Refresh the profile data to show updated status
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      // Handle specific error types
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to submit profile for verification. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'not_applied': { variant: 'default' as const, text: 'Not Applied' },
      'pending': { variant: 'warning' as const, text: 'Pending Review' },
      'approved': { variant: 'success' as const, text: 'Approved' },
      'rejected': { variant: 'error' as const, text: 'Rejected' },
      'auto_approved': { variant: 'success' as const, text: 'Auto Approved' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_applied;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  // Always show all tabs to give users choice of verification method
  const tabs: Array<{ id: 'email' | 'office' | 'contact' | 'document'; label: string; icon: string }> = [
    { id: 'email' as const, label: 'Email Verification', icon: '📧' },
    { id: 'office' as const, label: 'Office Details', icon: '🏢' },
    { id: 'contact' as const, label: 'Contact Info', icon: '📱' },
    { id: 'document' as const, label: 'Document Upload', icon: '📄' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 gap-3">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Law Enforcement Profile
            </h2>
            {profile && (
              <div className="flex items-center space-x-2">
                {getStatusBadge(profile.verificationStatus)}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {/* Verification Methods Status */}
            <div className="hidden sm:flex items-center space-x-2 text-sm">
              <div className={`flex items-center space-x-1 ${profile?.governmentEmailVerified ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Email</span>
              </div>
              <div className={`flex items-center space-x-1 ${profile?.governmentIdFile ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Document</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {currentMode === 'view' && profile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMode('edit')}
                >
                  Edit Profile
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto custom-scrollbar">
            <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {success && (
            <div className="mx-6 mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'email' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Government Email Verification
                </h3>
                
                {profile?.governmentEmailVerified && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <p className="text-green-800 dark:text-green-200 font-medium">
                        Email verified: {profile.governmentEmail}
                      </p>
                    </div>
                  </div>
                )}

                {!profile?.governmentEmailVerified && (
                  <div className="space-y-4">
                    {/* Show info message only if no email is set */}
                    {!profile?.governmentEmail && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-blue-800 dark:text-blue-200 font-medium mb-2">
                              Choose Your Verification Method
                            </p>
                            <p className="text-blue-700 dark:text-blue-300 text-sm mb-3">
                              You can verify your law enforcement status using either method:
                            </p>
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-blue-700 dark:text-blue-300 text-sm">1. Add government email and verify with OTP</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-blue-700 dark:text-blue-300 text-sm">2. Upload government-issued ID document</span>
                                <button
                                  onClick={() => setActiveTab('document')}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm font-medium underline"
                                >
                                  Go to Document Upload →
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Email verification form - always show if not verified */}
                    <div className="space-y-4">
                      {emailData.step === 'email' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Government Email Address
                            </label>
                            <input
                              type="email"
                              value={emailData.email}
                              onChange={(e) => handleEmailChange('email', e.target.value)}
                              placeholder="Enter your government email address"
                              disabled={isLoading || currentMode === 'view'}
                              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white ${
                                fieldErrors.email 
                                  ? 'border-red-300 dark:border-red-600' 
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                            {fieldErrors.email && (
                              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                {fieldErrors.email}
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={handleSendOTP}
                            disabled={isLoading || !emailData.email || currentMode === 'view'}
                            className="w-full"
                          >
                            {isLoading ? 'Sending...' : 'Send OTP'}
                          </Button>
                        </div>
                      )}

                      {emailData.step === 'otp' && (
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                              We sent a 6-digit verification code to:
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">{emailData.email}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Verification Code
                            </label>
                            <input
                              type="text"
                              value={emailData.otp}
                              onChange={(e) => handleEmailChange('otp', e.target.value)}
                              placeholder="Enter 6-digit code"
                              maxLength={6}
                              disabled={isLoading || currentMode === 'view'}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                            />
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => setEmailData(prev => ({ ...prev, step: 'email' }))}
                              disabled={isLoading || currentMode === 'view'}
                              className="flex-1"
                            >
                              Back
                            </Button>
                            <Button
                              onClick={handleVerifyOTP}
                              disabled={isLoading || emailData.otp.length !== 6 || currentMode === 'view'}
                              className="flex-1"
                            >
                              {isLoading ? 'Verifying...' : 'Verify Email'}
                            </Button>
                          </div>
                          
                          {/* Debug button for development */}
                          {process.env.NODE_ENV === 'development' && (
                            <div className="mt-3">
                              <Button
                                variant="outline"
                                onClick={handleGetCurrentOTP}
                                className="w-full text-xs"
                                size="sm"
                              >
                                🔍 Get Current OTP (Debug)
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            )}

            {activeTab === 'office' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Office Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Department Name
                    </label>
                    <input
                      type="text"
                      value={formData.departmentName}
                      onChange={(e) => handleInputChange('departmentName', e.target.value)}
                      placeholder="e.g., Cyber Crime Unit"
                      disabled={isLoading || currentMode === 'view'}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white ${
                        fieldErrors.departmentName 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {fieldErrors.departmentName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.departmentName}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rank/Position
                    </label>
                    <input
                      type="text"
                      value={formData.rankPosition}
                      onChange={(e) => handleInputChange('rankPosition', e.target.value)}
                      placeholder="e.g., Detective, Inspector"
                      disabled={isLoading || currentMode === 'view'}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white ${
                        fieldErrors.rankPosition 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {fieldErrors.rankPosition && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.rankPosition}
                      </p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Office Address
                  </label>
                  <textarea
                    value={formData.officeAddress}
                    onChange={(e) => handleInputChange('officeAddress', e.target.value)}
                    placeholder="Enter your office address"
                    rows={3}
                    disabled={isLoading || currentMode === 'view'}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white ${
                      fieldErrors.officeAddress 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {fieldErrors.officeAddress && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldErrors.officeAddress}
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Contact Information
                </h3>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    💡 <strong>Tip:</strong> If you use the same number for both WhatsApp and Telegram, you can copy it between the fields using the "Copy" buttons.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        WhatsApp Number
                      </label>
                      {formData.whatsappNumber && (
                        <button
                          type="button"
                          onClick={() => handleCopyNumber('whatsappNumber', 'telegramNumber')}
                          disabled={isLoading || currentMode === 'view'}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 disabled:opacity-50"
                        >
                          Copy to Telegram
                        </button>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={formData.whatsappNumber}
                      onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
                      placeholder="+1234567890"
                      disabled={isLoading || currentMode === 'view'}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white ${
                        fieldErrors.whatsappNumber 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {fieldErrors.whatsappNumber && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.whatsappNumber}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Telegram Number
                      </label>
                      {formData.telegramNumber && (
                        <button
                          type="button"
                          onClick={() => handleCopyNumber('telegramNumber', 'whatsappNumber')}
                          disabled={isLoading || currentMode === 'view'}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 disabled:opacity-50"
                        >
                          Copy to WhatsApp
                        </button>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={formData.telegramNumber}
                      onChange={(e) => handleInputChange('telegramNumber', e.target.value)}
                      placeholder="+1234567890"
                      disabled={isLoading || currentMode === 'view'}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white ${
                        fieldErrors.telegramNumber 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {fieldErrors.telegramNumber && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.telegramNumber}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'document' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Government ID Upload
                </h3>
                
                {!profile?.governmentEmailVerified && !profile?.governmentIdFile && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-blue-800 dark:text-blue-200 font-medium mb-1">
                          Alternative Verification Method
                        </p>
                        <p className="text-blue-700 dark:text-blue-300 text-sm">
                          You can use this method instead of or in addition to email verification. Upload your government-issued ID for verification.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {profile?.governmentIdFile && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-8 h-8 text-green-600 dark:text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Government ID uploaded</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">File ready for review</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!profile?.governmentIdFile && (
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(file);
                        }
                      }}
                      disabled={isLoading || currentMode === 'view'}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className={`cursor-pointer ${currentMode === 'view' ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Upload Government ID
                      </p>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Click to browse or drag and drop your file here
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isLoading || currentMode === 'view'}
                      >
                        Choose File
                      </Button>
                    </label>
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">File Requirements:</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Accepted formats: PDF, JPG, PNG</li>
                    <li>• Maximum file size: 10MB</li>
                    <li>• Clear, readable image of your government-issued ID</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {currentMode === 'edit' && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
                {/* Verification Status */}
                <div className="flex items-center space-x-4 text-sm">
                  <div className={`flex items-center space-x-1 ${profile?.governmentEmailVerified ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Email Verified</span>
                  </div>
                  <div className={`flex items-center space-x-1 ${profile?.governmentIdFile ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>ID Uploaded</span>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentMode('view')}
                    disabled={isLoading}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Draft'
                    )}
                  </Button>
                  <Button
                    onClick={handleSubmitForVerification}
                    disabled={isLoading || (!profile?.governmentEmailVerified && !profile?.governmentIdFile)}
                    className="w-full sm:w-auto"
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Submitting...
                      </>
                    ) : (
                      'Submit for Verification'
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Help Text */}
              {(!profile?.governmentEmailVerified && !profile?.governmentIdFile) && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                    <strong>Note:</strong> You need to complete either email verification or upload a government ID document before submitting for verification.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LawEnforcementProfileModal;
